import { gql, useMutation } from "@apollo/client";
import { Box, Button, createStyles, makeStyles, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@material-ui/core";
import React from "react";
import { useList } from "../../apollo/networking";
import { useKeysetPagination } from "../../apollo/networking/use-keyset-pagination";
import { useAppDialog } from "../../app-dialog";
import { AttendeeCertificateFragment, AttendeeCertificateModel } from "../../models/attendee-certificate.model";
import { CertificateModel } from "../../models/certificate.model";
import { ColumnSelector } from "../../ui/column-selector";
import { LoadingButton } from "../../ui/loading-button";
import { LoadingDots } from "../../ui/loading-dots";
import { CertificateEditorDialog } from "../event-certificates/certificate-editor";
import { CertificateAttendeeItem } from "./certificate-attendee-item";
import { ExportCertAttendees } from "./export-cert-attendees";
import { CertAttendeeCol, useCertAttendeeColumns } from "./use-cert-attendee-columns";



type Props = {
    certificate: CertificateModel;
}


const MutationAssign = gql`
    mutation upsert_ac($object: attendee_certificate_insert_input!, $certificate_id: bigint!) {
        insert_attendee_certificate_one(object: $object, on_conflict: {
            constraint: attendee_certificate_pkey,
            update_columns: [deleted_at]
        }) {
            id
            attendee {
                ${AttendeeCertificateFragment}
            }
        }
    }
`;


export const CertificateAttendeeList = ({
    certificate,
}: Props) => {
    const classes = useStyles({});
    const app_dialog = useAppDialog();
    const [show_export, setShowExport] = React.useState(false);
    const {
        showColumn,
        columns,
        toggleColumn,
    } = useCertAttendeeColumns();
    const [upsert] = useMutation(MutationAssign);
    const [show_edit, setShowEdit] = React.useState(false);
    const {
        items,
        loading,
        fetchMore,
        has_more,
    } = useKeysetPagination<AttendeeCertificateModel>({
        entity_name: 'attendee',
        fragment: AttendeeCertificateFragment,
        clause_outer: `
            $event_id: bigint!, 
            $certificate_id: bigint!
            $cursor: bigint!
        `,
        variables: {
            event_id: certificate.event_id,
            certificate_id: certificate.id,
            cursor: Number.MAX_SAFE_INTEGER,
        },
        query_inner: `
            where: {_and: [
                {ticket: {event_id: {_eq: $event_id}}},
                {id: {_lt: $cursor}},
            ]},
            order_by: {id: desc}
        `
    });

    const toggleAssign = async (item: AttendeeCertificateModel, assign: boolean) => {
        try {
            await upsert({
                variables: {
                    certificate_id: certificate.id,
                    object: {
                        attendee_id: item.id,
                        certificate_id: certificate.id,
                        deleted_at: assign ? null : 'now()',
                    }
                }
            })
        } catch (e: any) {
            app_dialog.showError(e);
        }
    }

    const loadMore = () => {
        if (items.length === 0) {
            return;
        }
        fetchMore({
            event_id: certificate.event_id,
            certificate_id: certificate.id,
            cursor: items[items.length - 1].id,
        })
    }

    return <>
        {show_export ? <ExportCertAttendees
            certificate_id={certificate.id}
            event_id={certificate.event.id}
            event_title={certificate.event.title}
            onClose={() => setShowExport(false)}
        /> : null}
        {show_edit ? <CertificateEditorDialog
            action='edit'
            event_id={certificate.event_id}
            item={certificate}
            onFinish={() => setShowEdit(false)}
            onClose={() => setShowEdit(false)}
        /> : null}
        <div className={classes.header}>
            <Button
                size='small'
                color='primary'
                onClick={() => setShowExport(true)}
                variant='contained'
                style={{
                    marginRight: 8,
                }}>
                Export
            </Button>
            <Button
                variant='outlined'
                size='small'
                onClick={() => setShowEdit(true)}>Edit Certificate</Button>

            <div style={{ flex: 1 }} />
            <ColumnSelector<CertAttendeeCol>
                showColumn={showColumn}
                toggleColumn={toggleColumn}
                columns={columns}
            />
        </div>
        <TableContainer>
            <Table className={classes.table} size="small" aria-label="a dense table">
                <TableHead>
                    <TableRow className='header'>
                        <TableCell />
                        <TableCell>Id</TableCell>
                        <TableCell>Name</TableCell>
                        {showColumn('email') ? <TableCell>Email</TableCell> : null}
                        <TableCell>Assigned</TableCell>
                        <TableCell>Link</TableCell>
                        <TableCell>Submission</TableCell>
                        {showColumn('ticket') ? <TableCell>Ticket</TableCell> : null}
                        {showColumn('agd_id') ? <TableCell>AGD Id</TableCell> : null}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {items.map((item) => <CertificateAttendeeItem
                        key={item.id}
                        item={item}
                        showColumn={showColumn}
                        toggleAssign={toggleAssign}
                    />)}
                </TableBody>
            </Table>
        </TableContainer>
        {has_more ? <Box py={2}>
            <LoadingButton
                loading={loading}
                onClick={loadMore}
                fullWidth>Load More...</LoadingButton>
        </Box> : null}

    </>
}

const useStyles = makeStyles(theme => createStyles({
    table: {
        width: '100%',
        minWidth: 700,
        background: 'white',
        border: `1px solid ${theme.palette.divider}`,
        '& .header': {
            background: theme.palette.background.paper,
        }
    },
    header: {
        display: 'flex',
        paddingBottom: theme.spacing(2),
    }
}))